
export class Section {
  constructor ({ employee, date, type } = {}) {
    this.employee = employee
    this.date = date
    this.type = type

    this.roster = date.rosters
      ?.find(roster => roster.idEmployee === employee.idEmployee)

    this.blocks = []
  }

  get key () {
    if (this.type === SectionType.employee) {
      const date = this?.date?.date?.value
      const employee = this.employee?.idEmployee

      return `${date}-${employee}`
    }

    if (this.type === SectionType.date) {
      const date = this?.date?.date?.value

      return date
    }

    return undefined
  }

  addBlock (block) {
    const oldLength = this.blocks.length
    const newLength = this.blocks.push(block)
  }

  removeBlock (index) {
    this.blocks.splice(index, 1)
  }

  calculateCompression () {
    for (const block of this.blocks) {
      block.resetPressure()
    }

    const blocks = this.blocks
      .sort((a, b) => b?.startTime - a?.startTime)

    const levels = this.divideBlocksIntoLevels(blocks)

    this.computeForwardBlocks(levels)

    for (const level of levels) {
      if (level) {
        for (const block of level) {
          this.computeBlockPressures(block)
        }

        for (const block of level) {
          this.computeBlockForwardBackCoordinates(block, 0, 0)
        }
      }
    }
  }

  findMaxLevel (block, levels) {
    let j = 0

    for (; j < levels.length; j++) {
      const levelBlocks = levels[j]
      const noCollisions = this.blocksColliding(block, levelBlocks).length === 0
      if (noCollisions) {
        break
      }
    }

    return j
  }

  divideBlocksIntoLevels (blocks) {
    const levels = []
    for (const block of blocks) {
      block.level = this.findMaxLevel(block, levels)

      if (!levels[block.level]) {
        levels.push([])
      }
      levels[block.level].push(block)
    }

    return levels
  }

  blocksColliding (block, otherBlocks) {
    const colliding = otherBlocks.filter(otherBlock => block.collides(otherBlock))
    return colliding
  }

  computeForwardBlocks (levels) {
    for (let i = 0; i < levels.length; i++) {
      const level = levels[i]
      for (const block of level) {
        const forwardBlocks = levels
          .slice(i + 1)
          .map(level2 => this.blocksColliding(block, level2))

        block.forwardBlocks = [].concat(...forwardBlocks)
      }
    }
  }

  computeBlockPressures (block) {
    if (block.forwardPressure) { // not already computed
      return
    }

    let forwardPressure = 0
    for (const forwardBlock of block.forwardBlocks) {
      this.computeBlockPressures(forwardBlock)

      forwardPressure = Math.max(
        forwardPressure,
        1 + forwardBlock.forwardPressure,
      )
    }

    block.forwardPressure = forwardPressure
  }

  computeBlockForwardBackCoordinates (block, seriesBackwardPressure, seriesBackwardCoord) {
    const forwardBlocks = block.forwardBlocks

    if (!block.forwardCoord) { // not already computed
      if (forwardBlocks.length === 0) {
        // if there are no forward segments, this segment should butt up against the edge
        block.forwardCoord = 1.0
      } else {
        // sort highest pressure first

        forwardBlocks.sort((block1, block2) => {
          if (block1.forwardPressure !== block2.forwardPressure) {
            return block1.forwardPressure > block2.forwardPressure ? -1 : 1
          } else if (block1.backwardCoord !== block2.backwardCoord) {
            return ((block1.backwardCoord || 0) < (block2.backwardCoord) || 0) ? -1 : 1
          }

          if (block1.forwardPressure === block2.forwardPressure) {
            return 0
          }

          return block1.forwardPressure < block2.forwardPressure ? 1 : -1
        })

        // this segment's forwardCoord will be calculated from the backwardCoord of the
        // highest-pressure forward segment.
        this.computeBlockForwardBackCoordinates(forwardBlocks[0], seriesBackwardPressure + 1, seriesBackwardCoord)

        block.forwardCoord = forwardBlocks[0].backwardCoord
      }

      // calculate the backwardCoord from the forwardCoord. consider the series
      block.backwardCoord = block.forwardCoord - ((block.forwardCoord - seriesBackwardCoord) / (parseFloat(seriesBackwardPressure) + 1))

      // use this segment's coordinates to computed the coordinates of the less-pressurized
      // forward segments
      for (let i = 0; i < forwardBlocks.length; i++) {
        this.computeBlockForwardBackCoordinates(forwardBlocks[i], 0, block.forwardCoord)
      }
    }
  }
}

export const SectionType = Object.freeze({
  date: 'date',
  employee: 'employee',
})
